<template>
  <div class="campsites-container">
    
    <!-- Date Selection Card -->
    <v-card class="date-selection-card mb-4 elevation-2">
      <v-card-title class="date-selection-header">
        <h3 class="earthwerk-font">Select Your Dates</h3>
      </v-card-title>
      <v-card-text>
        <v-alert 
          v-if="hasGlobalBookingDates" 
          type="info" 
          class="mb-3" 
          icon="mdi-calendar-check" 
          dense 
          dismissible>
          <strong>Your selected stay dates:</strong> {{ formattedGlobalCheckIn }} to {{ formattedGlobalCheckOut }}
          <span class="ml-2 grey--text text-caption">({{ globalBookingDays }} days)</span>
          <br>
          <small class="text-caption">You can modify dates below if needed (must be within your stay period).</small>
        </v-alert>

        <v-alert 
          v-if="hasGlobalGuests" 
          type="success" 
          class="mb-3" 
          icon="mdi-account-group" 
          dense 
          dismissible>
          <strong>Your selected guests:</strong> {{ formattedGlobalGuests }}
          <br>
          <small class="text-caption">Guest numbers will be pre-filled when adding accommodations.</small>
        </v-alert>

        <v-layout row wrap>
          <v-flex md6 sm12 class="pa-2">
            <v-dialog 
              ref="startDateDialog" 
              persistent 
              v-model="modalStartDate" 
              lazy 
              full-width 
              width="290px" 
              :return-value.sync="facility.start_date">
              <template v-slot:activator="{ on }">
                <v-text-field 
                  v-on="on" 
                  :label="hasGlobalBookingDates ? 'Start Date (Optional)' : 'Start Date *'" 
                  v-model="facility.start_date" 
                  readonly 
                  prepend-icon="event" 
                  :hint="hasGlobalBookingDates ? `Must be between ${formattedGlobalCheckIn} and ${formattedGlobalCheckOut}` : ''" 
                  persistent-hint 
                  class="date-input"></v-text-field>
              </template>
              <v-date-picker 
                v-model="facility.start_date" 
                scrollable 
                :min="minStartDate" 
                :max="maxStartDate" 
                @input="resetForAvailability()">
                <v-spacer></v-spacer>
                <v-btn flat color="primary" @click="modalStartDate = false">Cancel</v-btn>
                <v-btn flat color="primary" @click="$refs.startDateDialog.save(facility.start_date)">OK</v-btn>
              </v-date-picker>
            </v-dialog>
          </v-flex>
          
          <v-flex md6 sm12 class="pa-2">
            <v-dialog 
              ref="endDateDialog" 
              persistent 
              v-model="modalEndDate" 
              lazy 
              full-width 
              width="290px" 
              :return-value.sync="facility.end_date">
              <template v-slot:activator="{ on }">
                <v-text-field 
                  v-on="on" 
                  :label="hasGlobalBookingDates ? 'End Date (Optional)' : 'End Date *'" 
                  v-model="facility.end_date" 
                  readonly 
                  :disabled="!facility.start_date" 
                  prepend-icon="event" 
                  :hint="hasGlobalBookingDates ? `Must be after start date and within ${formattedGlobalCheckOut}` : ''" 
                  persistent-hint 
                  class="date-input"></v-text-field>
              </template>
              <v-date-picker 
                v-model="facility.end_date" 
                scrollable 
                :min="minEndDate" 
                :max="maxEndDate" 
                @input="searchFacilityAvailability()">
                <v-spacer></v-spacer>
                <v-btn flat color="primary" @click="modalEndDate = false">Cancel</v-btn>
                <v-btn flat color="primary" @click="$refs.endDateDialog.save(facility.end_date)">OK</v-btn>
              </v-date-picker>
            </v-dialog>
          </v-flex>
        </v-layout>
        
        <v-flex v-if="facility.start_date && facility.end_date && allAvailableCampsites && allAvailableCampsites.length > 0">
          <h4 class="earth-color">AVAILABLE ACCOMMODATIONS: {{ allAvailableCampsites.length }}</h4>
          <small class="slots-explanation">
            Showing accommodations available from {{ facility.start_date }} to {{ facility.end_date }}
          </small>
        </v-flex>
      </v-card-text>
    </v-card>

    <!-- SKELETON LOADER (Shows while searching) -->
    <div v-if="showFullPageLoader" class="grid-wrapper">
      <div v-for="n in 3" :key="'skeleton-'+n" class="grid-item">
        <div class="campsite-card skeleton-card">
          <div class="skeleton-image"></div>
          <div class="card-body">
            <div class="skeleton-line" style="width: 70%"></div>
            <div class="skeleton-line" style="width: 40%; margin-top: 10px;"></div>
            <div class="skeleton-line" style="width: 100%; height: 40px; margin-top: 20px; border-radius: 20px;"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- ACCOMMODATIONS GRID -->
    <div v-else-if="allAvailableCampsites && allAvailableCampsites.length > 0" class="grid-wrapper">
      <div 
        class="grid-item"
        v-for="camp in allAvailableCampsites" 
        :key="camp.public_id">
        
        <div class="campsite-card">
          <div class="card-image-wrap" @click="openInfo(camp)">
            <v-img 
              class="campsite-image" 
              :src="camp.thumbnail" 
              aspect-ratio="1.6" 
              gradient="to bottom, rgba(0,0,0,0.0), rgba(0,0,0,0.15)">
            </v-img>
            <div class="campsite-badge" v-if="camp.pricing_status === 'true'">
              {{ getBadgeText(camp) }}
            </div>
          </div>
          
          <div class="card-body">
            <div class="title-row">
              <h3 class="campsite-title earthwerk-font" @click="openInfo(camp)">
                {{ camp.name.toUpperCase() }}
              </h3>
              <button class="info-btn" @click="openInfo(camp)">
                <v-icon small color="#74a533">mdi-information-outline</v-icon>
              </button>
            </div>

            <div class="meta-row">
              <v-icon x-small color="#7b8f6b">mdi-account-group</v-icon>
              <span>Max {{ camp.maximum_guests }} guests</span>
              <template v-if="camp.quantity">
                <span class="mx-1">·</span>
                <v-icon x-small color="#7b8f6b">mdi-home-group</v-icon>
                <span>{{ camp.quantity }} units</span>
              </template>
            </div>

            <div class="price-row" v-if="camp.pricing_status === 'true'">
              <div class="price-line">
                <span>{{ isCampsite(camp) ? 'Adult Rate' : 'Base Price' }}</span>
                <span class="price-value">{{ $localCurrency }} {{ getAdultPriceDisplay(camp) }}</span>
              </div>
              <div class="price-line" v-if="isCampsite(camp) && getChildPriceDisplay(camp) > 0">
                <span>Child Rate</span>
                <span class="price-value">{{ $localCurrency }} {{ getChildPriceDisplay(camp) }}</span>
              </div>
            </div>

            <button 
              class="add-btn"
              :disabled="camp.pricing_status !== 'true'" 
              @click="openBookingDialog(camp)">
              <v-icon small color="white" class="mr-1">mdi-plus</v-icon>
              Add to Booking
            </button>
          </div>
        </div>

      </div>
    </div>

    <!-- PROMPT TO SELECT DATES -->
    <v-layout row wrap v-else-if="!showFullPageLoader && (!facility.start_date || !facility.end_date)">
      <v-flex xs12>
        <v-card class="text-center pa-5 empty-state-card">
          <v-icon size="64" color="grey lighten-2">event</v-icon>
          <h4 class="grey--text">Please select start and end dates to see available accommodations</h4>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- NO RESULTS FOUND -->
    <v-layout row wrap v-else-if="!showFullPageLoader && facility.start_date && facility.end_date && allAvailableCampsites.length === 0">
      <v-flex xs12>
        <v-card class="text-center pa-5 empty-state-card">
          <v-icon size="64" color="grey lighten-2">hotel</v-icon>
          <h4 class="grey--text">No accommodations available for selected dates</h4>
          <p class="grey--text">Please try different dates</p>
          <v-btn color="primary" class="earthwerk-font mt-3" @click="resetForAvailability">
            Change Dates
          </v-btn>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- INFO DIALOG -->
    <v-dialog v-model="infoDialog" persistent max-width="900px">
      <v-card class="info-dialog">
        <div class="gallery-section">
          <div v-if="infoImages && infoImages.length > 0" class="gallery-container">
            <div class="image-navigation">
              <button class="nav-button prev" @click="prevInfoImage" :disabled="currentInfoImageIndex === 0">
                <v-icon>chevron_left</v-icon>
              </button>
              <div class="image-wrapper">
                <img :src="infoImages[currentInfoImageIndex].path" class="gallery-image" alt="Facility image">
              </div>
              <button class="nav-button next" @click="nextInfoImage" :disabled="currentInfoImageIndex === infoImages.length - 1">
                <v-icon>chevron_right</v-icon>
              </button>
            </div>
            <div class="image-counter">
              {{ currentInfoImageIndex + 1 }} / {{ infoImages.length }}
            </div>
          </div>
          <div v-else class="single-image-container">
            <img :src="currentInfoCamp ? currentInfoCamp.thumbnail : ''" class="gallery-image" alt="Facility thumbnail">
          </div>
        </div>
        
        <v-card-text class="details-section">
          <div class="details-header">
            <h1 class="facility-title earthwerk-font">{{ currentInfoCamp ? currentInfoCamp.name.toUpperCase() : '' }}</h1>
            <div class="capacity-display">
              <div class="capacity-item">
                <span class="capacity-label">Maximum Capacity:</span>
                <span class="capacity-value">{{ currentInfoCamp ? currentInfoCamp.maximum_guests : '' }} Guests</span>
              </div>
            </div>
          </div>
          <div class="description-section" v-html="currentInfoCamp ? currentInfoCamp.description : ''"></div>
        </v-card-text>
        
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn flat @click="infoDialog = false" class="earthwerk-font">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- BOOKING DIALOG -->
    <v-dialog v-model="dialog" persistent max-width="800px" @keydown.esc="closeBookingDialog">
      <v-card class="booking-dialog">
        <v-card-title class="dialog-header">
          <h2 class="earthwerk-font">ADD TO BOOKING</h2>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeBookingDialog">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="dialog-content">
          <v-tabs color="primary" dark slider-color="primary">
            <v-tab key="availability" class="earthwerk-font">Accommodation Details</v-tab>
            <v-tab key="conservation" class="earthwerk-font">Conservation Fees</v-tab>
            
            <v-tab-item key="availability">
              <div class="tab-content">
                <div class="date-pickers">
                  <v-text-field label="Start Date" v-model="bookingDetails.start_date" readonly prepend-icon="event" class="date-input" disabled></v-text-field>
                  <v-text-field label="End Date" v-model="bookingDetails.end_date" readonly prepend-icon="event" class="date-input" disabled></v-text-field>
                </div>

                <v-flex v-if="bookingDetails.start_date && bookingDetails.end_date && selectedCamp">
                  <h4 class="earth-color">AVAILABLE SLOTS : {{ availableSlots }}</h4>
                  <small class="slots-explanation">
                    (Maximum Capacity: {{ selectedCamp.maximum_guests }} guests)
                  </small>
                </v-flex>

                <div class="participant-selectors" v-if="showDates && bookingDetails.start_date && bookingDetails.end_date">
                  <div class="selector-group">
                    <v-autocomplete label="Children" :items="childSlots" v-model="bookingDetails.children" prepend-icon="child_friendly" class="participant-input" @change="updateTotalGuests"></v-autocomplete>
                    <v-autocomplete label="Adults" :items="adultSlots" v-model="bookingDetails.adults" prepend-icon="person" class="participant-input" @change="updateTotalGuests"></v-autocomplete>
                  </div>
                </div>

                <div v-if="hasGlobalGuests && (bookingDetails.adults > 0 || bookingDetails.children > 0)" class="global-guests-info">
                  <v-alert type="info" dense class="mb-2">
                    <v-icon small left>mdi-account-multiple</v-icon>
                    Booking for: {{ bookingDetails.adults }} Adult{{ bookingDetails.adults !== 1 ? 's' : '' }} and {{ bookingDetails.children }} Child{{ bookingDetails.children !== 1 ? 'ren' : '' }}
                  </v-alert>
                </div>

                <div class="price-summary" v-if="(bookingDetails.adults > 0 || bookingDetails.children > 0) && bookingDetails.start_date && bookingDetails.end_date">
                  <h3 class="earthwerk-font">Price Summary</h3>
                  <div class="price-breakdown">
                    <div class="price-line" v-if="bookingDetails.adults > 0">
                      <span>Adults ({{ bookingDetails.adults }} × {{ $localCurrency }} {{ $roundOff(getAdultPrice()) }} × {{ getDays() }} {{ getDays() === 1 ? 'night' : 'nights' }})</span>
                      <span>{{ $localCurrency }} {{ $roundOff(calculateAdultTotal()) }}</span>
                    </div>
                    <div class="price-line" v-if="bookingDetails.children > 0">
                      <span>Children ({{ bookingDetails.children }} × {{ $localCurrency }} {{ $roundOff(getChildPrice()) }} × {{ getDays() }} {{ getDays() === 1 ? 'night' : 'nights' }})</span>
                      <span>{{ $localCurrency }} {{ $roundOff(calculateChildTotal()) }}</span>
                    </div>
                    
                    <div class="price-total" v-if="calculateTotalPrice() > 0">
                      <strong>Total Amount:</strong>
                      <strong>{{ $localCurrency }} {{ $roundOff(calculateTotalPrice()) }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </v-tab-item>

            <v-tab-item key="conservation">
              <div class="tab-content">
                <div class="notice-text">
                  * Add conservation and vehicle entry fees if applicable *
                </div>

                <div class="fee-sections">
                  <div class="fee-section">
                    <h3 class="earthwerk-font">Conservation entry fees</h3>
                    <guests></guests>
                  </div>
                  <div class="fee-section">
                    <h3 class="earthwerk-font">Vehicle entry fees</h3>
                    <vehicle />
                  </div>
                </div>
                <small class="required-notice">* These fees are separate from accommodation costs</small>
              </div>
            </v-tab-item>
          </v-tabs>
        </v-card-text>
        
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn flat @click="closeBookingDialog" class="cancel-btn earthwerk-font">Cancel</v-btn>
          <v-btn 
            color="primary" 
            @click="addFacility()" 
            class="reserve-btn earthwerk-font"
            :disabled="!bookingDetails.start_date || !bookingDetails.end_date || (bookingDetails.adults === 0 && bookingDetails.children === 0)">
            Add to Booking
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// YOUR ORIGINAL SCRIPT BLOCK UNTOUCHED
import Guests from '../Repeated/Guests'
import Vehicle from '../Repeated/Vehicle'

export default {
  name: 'Campsites',
  components: { Guests, Vehicle },
  data() {
    return {
      dialog: false,
      infoDialog: false,
      currentInfoCamp: null,
      currentInfoImageIndex: 0,
      selectedCamp: null,
      modalStartDate: false,
      modalEndDate: false,
      today: this.$moment().format('YYYY-MM-DD'),
      showDates: false,
      showFullPageLoader: false,
      facility: {
        start_date: '',
        end_date: '',
        check_in: '',
        check_out: '',
        children: 0,
        adults: 0,
        accommodation_discount: 0,
        extra_pax_adults: 0,
        extra_pax_children: 0,
        facility: {}
      },
      bookingDetails: {
        start_date: '',
        end_date: '',
        children: 0,
        adults: 0,
        accommodation_discount: 0,
        extra_pax_adults: 0,
        extra_pax_children: 0
      },
      lastSearchedRange: null
    }
  },

  created() {
    if (this.$store.dispatch) {
      if (this.$store._actions && this.$store._actions['get_active_camping']) {
        this.$store.dispatch('get_active_camping').catch(function () {})
      }
      if (this.$store._actions && this.$store._actions['get_active_accommodation']) {
        this.$store.dispatch('get_active_accommodation').catch(function () {})
      }
      if (this.$store._actions && this.$store._actions['get_all_facility_pricing_types']) {
        this.$store.dispatch('get_all_facility_pricing_types').catch(function () {})
      }
    }

    this.loadDatesFromGlobalStore()
    this.loadGuestsFromStorage()
    this.$root.$on('booking-dates-changed', this.onBookingDatesChanged)
  },

  mounted() {
    this.$nextTick(() => {
      this.autoSearchIfDatesAvailable()
    })
  },

  beforeDestroy() {
    this.$root.$off('booking-dates-changed', this.onBookingDatesChanged)
  },

  computed: {
    globalBookingDates() {
      if (this.$store.state.GlobalBookingStore && this.$store.state.GlobalBookingStore.booking_dates) {
        return this.$store.state.GlobalBookingStore.booking_dates
      }
      return null
    },

    hasGlobalBookingDates() {
      return this.globalBookingDates &&
             this.globalBookingDates.check_in && this.globalBookingDates.check_in !== '' &&
             this.globalBookingDates.check_out && this.globalBookingDates.check_out !== ''
    },

    formattedGlobalCheckIn() {
      if (!this.hasGlobalBookingDates) return ''
      return this.$moment(this.globalBookingDates.check_in).format('MMM DD, YYYY')
    },

    formattedGlobalCheckOut() {
      if (!this.hasGlobalBookingDates) return ''
      return this.$moment(this.globalBookingDates.check_out).format('MMM DD, YYYY')
    },

    globalBookingDays() {
      return this.globalBookingDates ? this.globalBookingDates.days : 0
    },

    minStartDate() {
      if (this.hasGlobalBookingDates) return this.globalBookingDates.check_in
      return this.today
    },

    maxStartDate() {
      if (this.hasGlobalBookingDates && this.globalBookingDates.check_out) {
        return this.$moment(this.globalBookingDates.check_out).subtract(1, 'days').format('YYYY-MM-DD')
      }
      return null
    },

    minEndDate() {
      if (this.facility.start_date) {
        return this.$moment(this.facility.start_date).add(1, 'days').format('YYYY-MM-DD')
      }
      if (this.hasGlobalBookingDates) {
        return this.$moment(this.globalBookingDates.check_in).add(1, 'days').format('YYYY-MM-DD')
      }
      return this.today
    },

    maxEndDate() {
      if (this.hasGlobalBookingDates) return this.globalBookingDates.check_out
      return null
    },

    globalGuests() {
      if (this.$store.state.GlobalBookingStore && this.$store.state.GlobalBookingStore.booking_guests) {
        return this.$store.state.GlobalBookingStore.booking_guests
      }
      return { adults: 0, children: 0, total: 0 }
    },

    hasGlobalGuests() {
      return this.globalGuests.total > 0
    },

    formattedGlobalGuests() {
      if (!this.hasGlobalGuests) return ''
      return this.globalGuests.adults + ' Adult' + (this.globalGuests.adults !== 1 ? 's' : '') +
             ' and ' + this.globalGuests.children + ' Child' + (this.globalGuests.children !== 1 ? 'ren' : '')
    },

    allAvailableCampsites() {
      var facilities = []

      if (this.$store.state.BookingStore && this.$store.state.BookingStore.available_facilities) {
        facilities = this.$store.state.BookingStore.available_facilities
      } else if (this.$store.state.FacilityStore && this.$store.state.FacilityStore.available_facilities) {
        facilities = this.$store.state.FacilityStore.available_facilities
      } else if (this.$store.state.available_facilities) {
        facilities = this.$store.state.available_facilities
      }

      if (!Array.isArray(facilities)) return []

      return facilities.filter(function (facility) {
        return facility.pricing_status === 'true'
      })
    },

    pricedFacility() {
      if (this.$store.state.FacilityStore) {
        return this.$store.state.FacilityStore.facility_with_pricing || this.selectedCamp
      }
      return this.selectedCamp
    },

    infoImages() {
      if (this.$store.state.FacilityStore) {
        return this.$store.state.FacilityStore.facility_images || []
      }
      return []
    },

    availableSlots() {
      if (!this.selectedCamp) return 0
      return Number(this.selectedCamp.maximum_guests || 0) * Number(this.selectedCamp.quantity || 1)
    },

    childSlots() {
      var max = this.calculateChildMax
      var slots = []
      for (var i = 0; i <= max; i++) slots.push(i)
      return slots
    },

    adultSlots() {
      var max = this.calculateAdultMax
      var slots = []
      for (var i = 0; i <= max; i++) slots.push(i)
      return slots
    },

    calculateAdultMax() {
      if (!this.selectedCamp) return 10
      var totalCapacity = Number(this.selectedCamp.maximum_guests || 0) * Number(this.selectedCamp.quantity || 1)
      var totalGuests = Number(this.total || 0)
      var currentAdults = Number(this.bookingDetails.adults || 0)
      return Math.max(0, (totalCapacity - totalGuests) + currentAdults)
    },

    calculateChildMax() {
      if (!this.selectedCamp) return 10
      var totalCapacity = Number(this.selectedCamp.maximum_guests || 0) * Number(this.selectedCamp.quantity || 1)
      var totalGuests = Number(this.total || 0)
      var currentChildren = Number(this.bookingDetails.children || 0)
      return Math.max(0, (totalCapacity - totalGuests) + currentChildren)
    },

    total() {
      return Number(this.bookingDetails.adults) + Number(this.bookingDetails.children)
    }
  },

  methods: {
    isCampsite(camp) {
      return (
        camp.facility_type_id === 'Camping Sites' ||
        camp.facility_type === 'Camping Sites' ||
        (camp.name && camp.name.toLowerCase().indexOf('campsite') !== -1)
      )
    },

    getBadgeText(camp) {
      if (!camp.price || camp.price.length === 0) return 'Price unavailable'
      
      var priceData = camp.price[0]
      
      if (this.isCampsite(camp)) {
        var child = this.$roundOff(this.convertCurrency(priceData.currency_type, Number(priceData.child_price || 0)).amount)
        var adult = this.$roundOff(this.convertCurrency(priceData.currency_type, Number(priceData.adult_price || 0)).amount)
        return `${this.$localCurrency} ${child} per child / ${this.$localCurrency} ${adult} per adult`
      } 
      
      // For Stables or other accommodations
      var basePrice = priceData.mandatory_price || priceData.price || priceData.adult_price || 0
      var convertedBase = this.$roundOff(this.convertCurrency(priceData.currency_type, Number(basePrice)).amount)
      return `From ${this.$localCurrency} ${convertedBase}`
    },

    getAdultPriceDisplay(camp) {
      if (!camp.price || camp.price.length === 0) return 0
      var priceData = camp.price[0]
      var amount = this.isCampsite(camp) ? priceData.adult_price : (priceData.mandatory_price || priceData.adult_price || priceData.price)
      return this.$roundOff(this.convertCurrency(priceData.currency_type, Number(amount || 0)).amount)
    },

    getChildPriceDisplay(camp) {
      if (!camp.price || camp.price.length === 0) return 0
      var priceData = camp.price[0]
      return this.$roundOff(this.convertCurrency(priceData.currency_type, Number(priceData.child_price || 0)).amount)
    },

    loadDatesFromGlobalStore() {
      if (this.hasGlobalBookingDates) {
        this.facility.start_date = this.globalBookingDates.check_in
        this.facility.end_date = this.globalBookingDates.check_out
        return
      }
      try {
        const userData = JSON.parse(localStorage.getItem('user_data') || '{}')
        if (userData.check_in && userData.check_out) {
          this.facility.start_date = userData.check_in
          this.facility.end_date = userData.check_out
        }
      } catch (e) {}
    },

    getStorageAdults() {
      try {
        const guests = JSON.parse(localStorage.getItem('guests') || '{}')
        if (guests.adults !== undefined && guests.adults !== null && guests.adults !== '') {
          return Number(guests.adults || 0)
        }
      } catch (e) {}

      try {
        const conservation = JSON.parse(localStorage.getItem('conservation_data') || '[]')
        if (Array.isArray(conservation) && conservation.length > 0) {
          return conservation.reduce(function (sum, fee) {
            const type = fee.payment_person ? fee.payment_person.toLowerCase() : ''
            if (type.includes('child') || type.includes('infant')) return sum
            return sum + Number(fee.payment_guests || 0)
          }, 0)
        }
      } catch (e) {}

      try {
        const userData = JSON.parse(localStorage.getItem('user_data') || '{}')
        return Number(userData.adults || 0)
      } catch (e) { return 0 }
    },

    getStorageChildren() {
      try {
        const guests = JSON.parse(localStorage.getItem('guests') || '{}')
        if (guests.children !== undefined && guests.children !== null && guests.children !== '') {
          return Number(guests.children || 0)
        }
      } catch (e) {}

      try {
        const conservation = JSON.parse(localStorage.getItem('conservation_data') || '[]')
        if (Array.isArray(conservation) && conservation.length > 0) {
          return conservation.reduce(function (sum, fee) {
            const type = fee.payment_person ? fee.payment_person.toLowerCase() : ''
            if (!type.includes('child')) return sum
            if (type.includes('infant')) return sum
            return sum + Number(fee.payment_guests || 0)
          }, 0)
        }
      } catch (e) {}

      try {
        const userData = JSON.parse(localStorage.getItem('user_data') || '{}')
        return Number(userData.children || 0)
      } catch (e) { return 0 }
    },

    loadGuestsFromStorage() {
      var adults = this.getStorageAdults()
      var children = this.getStorageChildren()

      if ((adults === 0 && children === 0) && this.hasGlobalGuests) {
        adults = this.globalGuests.adults
        children = this.globalGuests.children
      }

      if (adults > 0 || children > 0) {
        this.bookingDetails.adults = adults
        this.bookingDetails.children = children
      }
    },

    autoSearchIfDatesAvailable() {
      if (!this.facility.start_date || !this.facility.end_date) return
      const range = `${this.facility.start_date}|${this.facility.end_date}`
      if (this.lastSearchedRange === range) return
      this.searchFacilityAvailability()
    },

    onBookingDatesChanged(dates) {
      if (!dates || !dates.check_in || !dates.check_out) return
      const same = this.facility.start_date === dates.check_in &&
                   this.facility.end_date === dates.check_out
      if (same) return
      this.facility.start_date = dates.check_in
      this.facility.end_date = dates.check_out
      this.searchFacilityAvailability()
    },

    updateTotalGuests() {},

    searchFacilityAvailability() {
      var self = this

      if (!this.facility.start_date || !this.facility.end_date) return

      const range = `${this.facility.start_date}|${this.facility.end_date}`
      this.lastSearchedRange = range

      var actionToUse = null
      if (this.$store._actions && this.$store._actions['check_date_facility_public_availability']) {
        actionToUse = 'check_date_facility_public_availability'
      } else if (this.$store._actions && this.$store._actions['check_date_facility_availability']) {
        actionToUse = 'check_date_facility_availability'
      }

      if (!actionToUse) return

      this.showFullPageLoader = true

      if (this.$store.commit) {
        this.$store.commit('REMOVE_AVAILABLE_FACILITIES')
      }

      this.$store.dispatch(actionToUse, {
        check_in_date: this.facility.start_date,
        check_out_date: this.facility.end_date
      })
      .then(function () {
        self.showFullPageLoader = false
      })
      .catch(function () {
        self.showFullPageLoader = false
      })
    },

    resetForAvailability() {
      this.facility.end_date = ''
      if (this.$store.commit) {
        this.$store.commit('REMOVE_AVAILABLE_FACILITIES')
      }
      this.selectedCamp = null
      this.showFullPageLoader = false
    },

    openInfo(camp) {
      this.currentInfoCamp = camp
      this.currentInfoImageIndex = 0
      if (this.$store.dispatch && this.$store._actions && this.$store._actions['get_facility_images']) {
        this.$store.dispatch('get_facility_images', camp.public_id)
      }
      this.infoDialog = true
    },

    prevInfoImage() {
      if (this.currentInfoImageIndex > 0) this.currentInfoImageIndex--
    },

    nextInfoImage() {
      if (this.currentInfoImageIndex < this.infoImages.length - 1) this.currentInfoImageIndex++
    },

    openBookingDialog(camp) {
      this.selectedCamp = camp

      var storedAdults = this.getStorageAdults()
      var storedChildren = this.getStorageChildren()

      this.bookingDetails = {
        start_date: this.facility.start_date,
        end_date: this.facility.end_date,
        children: storedChildren || this.globalGuests.children || 0,
        adults: storedAdults || this.globalGuests.adults || 0,
        accommodation_discount: 0,
        extra_pax_adults: 0,
        extra_pax_children: 0
      }

      this.showDates = true
      this.dialog = true

      if (this.$store.dispatch && this.$store._actions && this.$store._actions['get_facility']) {
        this.$store.dispatch('get_facility', camp.public_id)
      }

      if (this.$store.dispatch && this.$store._actions && this.$store._actions['get_facility_with_pricing']) {
        this.$store.dispatch('get_facility_with_pricing', {
          date: this.$moment(this.facility.start_date).format('YYYY-MM-DD'),
          public_id: camp.public_id
        })
      }
    },

    closeBookingDialog() {
      this.dialog = false
      this.selectedCamp = null
      this.showDates = false
    },

    getDays() {
      if (!this.bookingDetails.start_date || !this.bookingDetails.end_date) return 1
      var days = this.$moment(this.bookingDetails.end_date).diff(this.$moment(this.bookingDetails.start_date), 'days')
      return days > 0 ? days : 1
    },

    getAdultPrice() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]
      var amount = this.isCampsite(this.pricedFacility) ? priceData.adult_price : (priceData.mandatory_price || priceData.adult_price || priceData.price)
      var converted = this.convertCurrency(priceData.currency_type, Number(amount || 0))
      return converted ? converted.amount : 0
    },

    getChildPrice() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]
      var converted = this.convertCurrency(priceData.currency_type, Number(priceData.child_price || 0))
      return converted ? converted.amount : 0
    },

    calculateAdultTotal() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]
      var amount = this.isCampsite(this.pricedFacility) ? priceData.adult_price : (priceData.mandatory_price || priceData.adult_price || priceData.price)
      var total = Number(amount || 0) * Number(this.bookingDetails.adults || 0) * this.getDays()
      var converted = this.convertCurrency(priceData.currency_type, total)
      return converted ? converted.amount : 0
    },

    calculateChildTotal() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]
      var total = Number(priceData.child_price || 0) * Number(this.bookingDetails.children || 0) * this.getDays()
      var converted = this.convertCurrency(priceData.currency_type, total)
      return converted ? converted.amount : 0
    },

    calculateTotalPrice() {
      if (!this.pricedFacility || !this.pricedFacility.price || this.pricedFacility.price.length === 0) return 0
      var priceData = this.pricedFacility.price[0]
      var amount = this.isCampsite(this.pricedFacility) ? priceData.adult_price : (priceData.mandatory_price || priceData.adult_price || priceData.price)
      
      var adultPrice = Number(amount || 0)
      var childPrice = Number(priceData.child_price || 0)
      var adults = Number(this.bookingDetails.adults || 0)
      var children = Number(this.bookingDetails.children || 0)
      var days = this.getDays()
      var discount = Number(this.bookingDetails.accommodation_discount || 0)

      var total = (adultPrice * adults + childPrice * children) * days
      if (discount > 0) total = total * (1 - discount / 100)

      var convertedAmount = this.convertCurrency(priceData.currency_type, total)
      return convertedAmount ? convertedAmount.amount : 0
    },

    convertCurrency(currencyType, amount) {
      if (this.convert1) return this.convert1(currencyType, amount)
      return { amount: amount }
    },

    addFacility() {
      if (!this.selectedCamp) { alert('No accommodation selected'); return }
      if (!this.bookingDetails.start_date || !this.bookingDetails.end_date) { alert('You need a start and end date to proceed'); return }
      if (this.bookingDetails.start_date === this.bookingDetails.end_date) { alert('Check-out date must be after check-in date'); return }
      if (this.bookingDetails.adults === 0 && this.bookingDetails.children === 0) { alert('Please select at least one adult or child'); return }

      if (this.selectedCamp && this.selectedCamp.maximum_guests) {
        var totalCapacity = Number(this.selectedCamp.maximum_guests) * Number(this.selectedCamp.quantity || 1)
        if (this.total > totalCapacity) { alert('Maximum capacity is ' + totalCapacity + ' guests'); return }
      }

      var facilityData = this.selectedCamp
      var priceData = facilityData.price && facilityData.price[0] ? facilityData.price[0] : null
      if (!priceData) { alert('No pricing available for the selected dates'); return }

      var amount = this.isCampsite(facilityData) ? priceData.adult_price : (priceData.mandatory_price || priceData.adult_price || priceData.price)

      var bookingData = {
        public_id: facilityData.public_id,
        catering_type: '',
        facility_type: facilityData.facility_type_id,
        accommodation_type: facilityData.accomodation_type_id,
        start_date: this.bookingDetails.start_date,
        end_date: this.bookingDetails.end_date,
        name: facilityData.name,
        children: Number(this.bookingDetails.children),
        adults: Number(this.bookingDetails.adults),
        extra_pax_adults: Number(this.bookingDetails.extra_pax_adults),
        extra_pax_children: Number(this.bookingDetails.extra_pax_children),
        accommodation_discount: Number(this.bookingDetails.accommodation_discount),
        number_of_rooms: 1,
        price: this.calculateTotalPrice(),
        currency: priceData.currency_type,
        adult_price: Number(amount || 0),
        child_price: Number(priceData.child_price || 0),
        mandatory_price: Number(priceData.mandatory_price || 0),
        maximum_guests: Number(facilityData.maximum_guests),
        quantity: Number(facilityData.quantity || 1),
        nights: this.getDays()
      }

      var existingData = JSON.parse(localStorage.getItem('facility_data') || '[]')
      existingData.push(bookingData)
      localStorage.setItem('facility_data', JSON.stringify(existingData))

      this.$root.$emit('cart-updated')

      if (this.$store.commit) this.$store.commit('ADD_FACILITY', bookingData)

      this.storeConservation()
      this.storeVehicles()

      alert(facilityData.name + ' has been added to your booking')
      this.closeBookingDialog()
    },

    storeConservation() {
      var conservationData = []
      if (this.$store.state.FacilityStore) {
        conservationData = this.$store.state.FacilityStore.conservation_data || []
      }
      if (conservationData.length > 0) {
        localStorage.setItem('conservation_data', JSON.stringify(conservationData))
      }
    },

    storeVehicles() {
      var vehicleData = []
      if (this.$store.state.FacilityStore) {
        vehicleData = this.$store.state.FacilityStore.vehicle_data || []
      }
      if (vehicleData.length > 0) {
        localStorage.setItem('vehicle_data', JSON.stringify(vehicleData))
      }
    }
  },

  watch: {
    globalBookingDates: {
      handler: function (newDates) {
        if (!newDates || !newDates.check_in || !newDates.check_out) return

        var same = this.facility.start_date === newDates.check_in &&
                   this.facility.end_date === newDates.check_out
        if (same) return

        this.facility.start_date = newDates.check_in
        this.facility.end_date = newDates.check_out
        this.searchFacilityAvailability()
      },
      deep: true
    },

    globalGuests: {
      handler: function (newGuests) {
        if (!newGuests) return
        if (this.bookingDetails.adults === 0 && this.bookingDetails.children === 0 &&
            (newGuests.adults > 0 || newGuests.children > 0)) {
          this.bookingDetails.adults = newGuests.adults
          this.bookingDetails.children = newGuests.children
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
@font-face {
  font-family: 'Earthwerk';
  src: url('PizzaDude.dk-Earthwerk.otf');
  font-weight: normal;
  font-style: normal;
}

.campsites-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  position: relative;
}

.date-selection-card {
  border-radius: 12px;
  overflow: hidden;
}

.date-selection-header {
  background-color: #f5f5f5;
  border-bottom: 1px solid #eee;
}

.date-selection-header h3 {
  margin: 0;
  color: #74a533;
}

.earth-color {
  color: #74a533;
  margin-left: 5%;
}

.slots-explanation {
  color: #666;
  font-size: 12px;
  margin-left: 5%;
  display: block;
}

/* ---------------------------------------------------
   UI FIX: App-style Responsive Grid
--------------------------------------------------- */
.grid-wrapper {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-top: 10px;
}

@media (min-width: 600px) {
  .grid-wrapper {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 960px) {
  .grid-wrapper {
    grid-template-columns: repeat(3, 1fr);
  }
}

.grid-item {
  display: flex;
}

.campsite-card {
  background: rgba(255, 255, 255, 0.96) !important;
  border-radius: 18px !important;
  overflow: hidden;
  border: 1px solid rgba(165, 214, 167, 0.4);
  box-shadow: 0 6px 18px -8px rgba(0, 0, 0, 0.25) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.campsite-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 26px -10px rgba(0, 0, 0, 0.3) !important;
}

.card-image-wrap {
  position: relative;
  width: 100%;
  cursor: pointer;
  overflow: hidden;
}

.campsite-image {
  border-radius: 0 !important;
  display: block;
  transition: transform 0.4s ease;
}

.campsite-card:hover .campsite-image {
  transform: scale(1.04);
}

.campsite-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: #8cc63f;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 12px;
  border-radius: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  letter-spacing: 0.2px;
}

.card-body {
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.campsite-title {
  flex: 1;
  margin: 0;
  font-family: 'Earthwerk', sans-serif;
  font-size: 15px;
  line-height: 1.25;
  font-weight: 700;
  color: #2d4a1e;
  letter-spacing: 0.4px;
  cursor: pointer;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.15s ease;
}

.campsite-title:hover {
  color: #74a533 !important;
}

.info-btn {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #eef3e8;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
}

.info-btn:hover {
  background: #dbe8cc;
}

.meta-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 11.5px;
  color: #7b8f6b;
}

.price-row {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #eef1ea;
}

.price-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12.5px;
  color: #5e6e4e;
  padding: 2px 0;
  gap: 8px;
}

.price-value {
  font-weight: 700;
  color: #e67e22;
  white-space: nowrap;
}

.add-btn {
  width: 100%;
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #74a533;
  color: #fff;
  font-family: 'Earthwerk', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 12px 20px;
  border: none;
  border-radius: 40px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(116, 165, 51, 0.3);
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  margin-top: auto;
}

.add-btn:active {
  transform: scale(0.98);
}

.add-btn:disabled {
  background: #dce5d4;
  box-shadow: none;
  cursor: not-allowed;
}

/* Skeleton Loader */
.skeleton-card {
  pointer-events: none;
  border-color: #f0f0f0;
  background: rgba(255, 255, 255, 0.96);
}
.skeleton-image {
  width: 100%;
  height: 180px;
  background: #eef1f4;
  animation: pulse 1.5s infinite;
}
.skeleton-line {
  height: 16px;
  background: #eef1f4;
  border-radius: 4px;
  animation: pulse 1.5s infinite;
}
@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

.empty-state-card {
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

/* Dialog Styles (Unchanged) */
.earthwerk-font {
  font-family: 'Earthwerk', sans-serif;
  color: #333;
  font-size: 20px;
  line-height: 1.3;
  margin-bottom: 8px;
  letter-spacing: 0.5px;
}

.info-dialog, .booking-dialog {
  border-radius: 12px !important;
  overflow: hidden;
}

.gallery-section {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
}

.gallery-container, .single-image-container {
  width: 100%;
  height: 400px;
}

.image-navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  position: relative;
}

.image-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gallery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.7);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
}

.nav-button:hover {
  background: rgba(255, 255, 255, 0.9);
}

.nav-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.nav-button.prev { left: 20px; }
.nav-button.next { right: 20px; }

.image-counter {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 14px;
  z-index: 10;
}

.details-section {
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.details-header {
  margin-bottom: 25px;
  border-bottom: 1px solid #eee;
  padding-bottom: 20px;
}

.facility-title {
  font-family: 'Earthwerk', sans-serif;
  color: #333;
  font-size: 32px;
  margin-bottom: 15px;
  letter-spacing: 0.5px;
}

.description-section {
  color: #555;
  line-height: 1.8;
  font-size: 16px;
  margin-bottom: 30px;
}

.dialog-header {
  background-color: #74a533;
  color: white;
  padding: 20px 24px;
}

.dialog-header h2 {
  font-family: 'Earthwerk', sans-serif;
  font-size: 24px;
  margin: 0;
}

.dialog-content {
  padding: 24px;
}

.tab-content {
  padding: 15px 0;
}

.notice-text {
  color: #666;
  font-size: 14px;
  margin-bottom: 20px;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 6px;
}

.fee-sections {
  display: grid;
  gap: 25px;
}

.fee-section {
  background: #f9f9f9;
  padding: 15px;
  border-radius: 8px;
}

.fee-section h3 {
  margin: 0 0 15px;
  color: #444;
  font-size: 18px;
}

.required-notice {
  color: #999;
  font-size: 12px;
  display: block;
  margin-top: 10px;
}

.global-guests-info {
  margin: 15px 0;
}

.date-pickers {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 25px;
}

.date-input {
  margin-bottom: 15px;
}

.participant-selectors {
  margin: 20px 0;
}

.selector-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.participant-input {
  margin-bottom: 15px;
}

.price-summary {
  margin-top: 25px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.price-breakdown {
  margin-top: 10px;
}

.price-line {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.price-total {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 2px solid #74a533;
  font-size: 16px;
  font-weight: bold;
}

.dialog-actions {
  padding: 16px 24px;
  border-top: 1px solid #eee;
}

.cancel-btn {
  margin-right: 10px;
}

.reserve-btn {
  font-weight: 600;
  letter-spacing: 0.5px;
}

@media (max-width: 960px) {
  .earthwerk-font { font-size: 18px; }
  .date-pickers, .selector-group { grid-template-columns: 1fr; }
  .capacity-display { flex-direction: column; gap: 10px; }
  .gallery-container, .single-image-container { height: 300px; }
  .facility-title { font-size: 28px; }
}

@media (max-width: 600px) {
  .campsites-container { padding: 12px 8px; }
  .grid-wrapper { gap: 12px; }
  .card-body { padding: 12px 14px 14px; }
  .campsite-title { font-size: 14px; }
  .campsite-badge { font-size: 11px; padding: 4px 10px; top: 10px; left: 10px; }
  .price-line { font-size: 12px; }
  .add-btn { font-size: 13px; padding: 10px 16px; }

  .gallery-container, .single-image-container { height: 250px; }
  .facility-title { font-size: 24px; }
  .nav-button { width: 35px; height: 35px; }
  .nav-button.prev { left: 10px; }
  .nav-button.next { right: 10px; }
  .image-counter { font-size: 12px; padding: 3px 10px; }
}
</style>